import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import useWindowDimensions from "../../common/useWindowDimensions";
import {
  MuteHistoryEvent,
  MUTE_HISTORY_DAYS,
} from "./ActionLogsUtils/handleFetchMuteHistoryData";
import { formatSiteTimestamp } from "./ActionLogsUtils/formatSiteTimestamp";

// PROD-27: one row per mute/unmute action. Site-scope mutes list every
// camera they covered; we show the first few names and tooltip the rest.

const HEADERS = [
  "Date",
  "Action",
  "Scope",
  "Cameras",
  "Duration",
  "Until",
  "User",
];

const MAX_INLINE_CAMERAS = 3;

export function formatEpoch(
  epochSeconds: number | null,
  siteTimezone: string | null = null
): string {
  if (epochSeconds == null) return "-";
  return formatSiteTimestamp(new Date(epochSeconds * 1000), siteTimezone);
}

export function formatDuration(minutes: number | null): string {
  if (minutes == null) return "-";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

export function cameraLabel(
  event: MuteHistoryEvent,
  cameraNames: Map<string, string>
): { inline: string; tooltip: string } {
  const names = (event.camera_ids ?? []).map(
    (id) => cameraNames.get(String(id)) ?? `#${id}`
  );
  const shown = names.slice(0, MAX_INLINE_CAMERAS).join(", ");
  const extra = names.length - MAX_INLINE_CAMERAS;
  return {
    inline: extra > 0 ? `${shown} +${extra} more` : shown || "-",
    tooltip: names.join(", "),
  };
}

export const MuteHistoryTable = ({
  muteHistoryResults,
  cameraNames,
  unavailable = false,
  siteTimezone = null,
}: {
  muteHistoryResults: MuteHistoryEvent[];
  cameraNames: Map<string, string>;
  unavailable?: boolean;
  siteTimezone?: string | null;
}) => {
  const { height, width } = useWindowDimensions();
  const [calculatedHeight, setCalculatedHeight] = React.useState(0);
  const [calculatedWidth, setCalculatedWidth] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const rows = muteHistoryResults;

  const pageClamped = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1)
  );
  const visibleRows = React.useMemo(
    () =>
      rows.slice(
        pageClamped * rowsPerPage,
        pageClamped * rowsPerPage + rowsPerPage
      ),
    [rows, pageClamped, rowsPerPage]
  );

  React.useEffect(() => {
    setCalculatedHeight(height - 205);
    setCalculatedWidth(width - 150);
  }, [height, width]);

  React.useEffect(() => {
    if (page > pageClamped) setPage(pageClamped);
  }, [pageClamped, page]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer
        sx={{ maxHeight: calculatedHeight, minWidth: calculatedWidth }}
      >
        <Table
          sx={{ fontFamily: "Mulish" }}
          aria-label="mute history table"
          stickyHeader
        >
          <TableHead>
            <TableRow>
              {HEADERS.map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    fontSize: "15px",
                    fontFamily: "Mulish",
                    fontWeight: "Bold",
                  }}
                  align="left"
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{ fontFamily: "Mulish" }}
                >
                  {unavailable
                    ? "Mute history is unavailable right now."
                    : `No mutes or unmutes in the last ${MUTE_HISTORY_DAYS} days.`}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((event, index) => {
                const cams = cameraLabel(event, cameraNames);
                return (
                  <TableRow
                    key={`${event.ts}-${event.action}-${event.camera_pk}-${index}`}
                  >
                    <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                      {formatEpoch(event.ts, siteTimezone)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Mulish",
                        fontWeight: "Bold",
                        color: event.action === "mute" ? "#d32f2f" : "#2e7d32",
                      }}
                      align="left"
                    >
                      {event.action === "mute" ? "Muted" : "Unmuted"}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                      {event.scope === "site" ? "Site" : "Camera"}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                      <Tooltip title={cams.tooltip}>
                        <span>{cams.inline}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                      {formatDuration(event.duration_minutes)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                      {formatEpoch(event.expires_at, siteTimezone)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                      {event.user ?? "-"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={pageClamped}
        onPageChange={(_e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
};
